import React, { memo, useCallback } from 'react';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import {
  DownloadErrorCodes,
  DownloadStatus,
  ILessonPlan,
  ISchoolClassLessonStatus,
  lessonPlanDetailRecord,
} from '@taleemabad/db';
import { Spacing } from '../../atoms/spacing/spacing';
import {
  AnimatedDots,
  Button,
  Icon,
  IconText,
  List,
  ListExtended,
  ListExtendedText,
  StyledDiv,
  Typography,
} from '@taleemabad/dsm/components';
import { formatUtcDate, useNetworkStatus } from '@taleemabad/shared';
import { Capacitor } from '@capacitor/core';
import { removeUrduCharacters } from '@taleemabad/dsm/utils';

export interface LpSelected {
  data: ILessonPlan;
  index: number;
}
interface LpCardListProps {
  lessonPlans: ILessonPlan[];
  schoolClass: number;
  chapterId: number;
  lpDownloadEnabled?: boolean;
  lpDownloadAbTestEnable?: boolean;
  onLpSelected?: (lesson: LpSelected) => void;
  onTrailingIconClick?: (lesson: LpSelected) => void;
  onLpDelete?: (lesson: ILessonPlan) => void;
  onRetryDownloadLp?: (lesson: ILessonPlan) => void;
  onCancelLp?: (lesson: ILessonPlan) => void;
}
interface LpCardWrapperProps {
  onLpSelected?: (lesson: LpSelected) => void;
  onTrailingIconClick?: (lesson: LpSelected) => void;
  onLpDelete?: () => void;
  onRetryDownloadLp?: () => void;
  onCancelLp?: () => void;
  lessonPlan: ILessonPlan;
  chapterId: number;
  index: number;
  lessonPlanDetail?: lessonPlanDetailRecord;
  schoolClass: number;
  lpDownloadEnabled?: boolean;
  lpDownloadAbTestEnabled?: boolean;
}

export interface LpSelected {
  data: ILessonPlan;
  index: number;
}
const LpCardListWrapper = styled.div`
  display: grid;
  gap: var(--rk-spacing-xl);
  grid-template-columns: repeat(3, 1fr);

  ${mq(BreakpointSizes.xs)} {
    grid-template-columns: repeat(1, 1fr);
  }
  ${mq(BreakpointSizes.sm)} {
    grid-template-columns: repeat(2, 1fr);
  }
`;
const DownloadNames = {
  [DownloadStatus.None]: '',
  [DownloadStatus.Downloading]: 'Downloading',
  [DownloadStatus.Completed]: 'Downloaded',
  [DownloadStatus.Failed]: 'Failed',
  [DownloadStatus.Paused]: 'Download paused',
};
const DownloadingStatus = [
  DownloadStatus.Downloading,
  DownloadStatus.Added,
  DownloadStatus.Queued,
];
const DownloadStatusIcon = {
  [DownloadStatus.Completed]: 'delete',
  [DownloadStatus.Failed]: 'sync',
};
const DownloadStatusText = {
  [DownloadStatus.Completed]: 'Delete Download',
  [DownloadStatus.Failed]: 'Retry Download',
};
const DownloadStatusColor = {
  [DownloadStatus.Completed]: '--md-sys-color-schemes-error',
  [DownloadStatus.Failed]: '--md-ref-primary-40 ',
};
const isDownloading = (lessonPlan: ILessonPlan, isConnected: boolean) => {
  return (
    lessonPlan?.downloadStatus &&
    DownloadingStatus.includes(lessonPlan.downloadStatus as DownloadStatus) &&
    isConnected
  );
};
const listHeading = (
  lessonPlan: ILessonPlan,
  lpDownloadEnabled: boolean,
  lpDownloadAbTestEnable: boolean,
  isConnected: boolean,
  index: number,
) => {
  return Capacitor.isNativePlatform() && lpDownloadEnabled ? (
    <StyledDiv flexDirection="row" css={{ overflow: 'hidden' }}>
      {`Lesson ${index}`}
      {lessonPlan?.downloadStatus &&
        lessonPlan?.downloadStatus !== DownloadStatus.None &&
        !lpDownloadAbTestEnable && (
          <Typography
            fullWidth
            variant={
              (lessonPlan?.downloadStatus === DownloadStatus.Downloading &&
                !isConnected) ||
              lessonPlan?.downloadStatus === DownloadStatus.Failed
                ? 'textBodyLargeError'
                : 'textBodyLargeSecondary'
            }
          >
            {`\u00A0- ${lessonPlan?.downloadStatus === DownloadStatus.Downloading && !isConnected ? DownloadNames[DownloadStatus.Paused] : DownloadNames[lessonPlan?.downloadStatus as keyof typeof DownloadNames]} `}
            {isDownloading(lessonPlan, isConnected) && <AnimatedDots />}
          </Typography>
        )}
    </StyledDiv>
  ) : (
    `Lesson ${index}`
  );
};

const lpCardMessages = (lessonPlan: ILessonPlan, isConnected: boolean) => {
  const { downloadStatus, downloadErrorCode } = lessonPlan || {};

  const isDownloading =
    downloadStatus === DownloadStatus.Downloading ||
    downloadStatus === DownloadStatus.Added ||
    downloadStatus === DownloadStatus.Queued;

  if (
    (isDownloading && !isConnected) ||
    downloadStatus === DownloadStatus.Failed
  ) {
    let message = 'Lesson failed to download. Try again.';

    if (isDownloading) {
      message = 'Internet disconnected. Connect to continue.';
    } else if (downloadErrorCode === DownloadErrorCodes.fileAllocationFailed) {
      message = 'Low memory. Clear memory and try again.';
    }

    return (
      <Spacing marginTop="lg">
        <IconText
          textVariant="textBodySmallSchemesError"
          iconColor="--md-sys-color-schemes-error"
          icon="error"
          text={message}
        />
      </Spacing>
    );
  }

  return null;
};

const lpExtendedMessage = (
  lessonPlan: ILessonPlan,
  isConnected: boolean,
  index: number,
) => {
  if (lessonPlan.downloadStatus === DownloadStatus.Completed) {
    return (
      <IconText
        textVariant="textBodyLargeSecondary"
        iconColor="--md-sys-color-secondary"
        icon="check"
        text={`Lesson ${index} can be viewed offline`}
      />
    );
  }
  if (lessonPlan.downloadStatus === DownloadStatus.Failed) {
    if (
      lessonPlan.downloadErrorCode === DownloadErrorCodes.fileAllocationFailed
    ) {
      return (
        <IconText
          textVariant="textBodySmallSchemesError"
          iconColor="--md-sys-color-schemes-error"
          icon="error"
          text="No memory. Clear memory & try again."
        />
      );
    }

    return (
      <IconText
        textVariant="textBodySmallSchemesError"
        iconColor="--md-sys-color-schemes-error"
        icon="error"
        text={`Lesson ${index} failed to download`}
      />
    );
  }
  if (lessonPlan.downloadStatus === DownloadStatus.Downloading) {
    if (!isConnected) {
      return (
        <IconText
          textVariant="textBodySmallSchemesError"
          iconColor="--md-sys-color-schemes-error"
          icon="error"
          text="Internet disconnected. Connect to continue."
        />
      );
    }
    return (
      <>
        {`Lesson ${index} is downloading`} <AnimatedDots />
      </>
    );
  }
};

const LpCardWrapper: React.FC<LpCardWrapperProps> = memo(
  ({
    lessonPlan,
    chapterId,
    index,
    schoolClass,
    lpDownloadEnabled = false,
    lpDownloadAbTestEnabled = false,
    onLpSelected,
    onLpDelete,
    onTrailingIconClick,
    onRetryDownloadLp,
  }) => {
    const { isConnected } = useNetworkStatus();
    const status = (data: ISchoolClassLessonStatus[]) => {
      return data.find((c) => c.schoolClass === schoolClass)?.status as string;
    };
    const bookInfo = lessonPlan.bookInfo.filter(
      (info) => info.bookChapter === chapterId,
    )?.[0];
    const setTrailingIcon = useCallback(
      (lesson: ILessonPlan) => {
        if (!Capacitor.isNativePlatform()) return {};
        if (!lpDownloadEnabled) return {};
        if (lpDownloadAbTestEnabled) return {};
        const downloadStatus = lesson?.downloadStatus;
        let trailingIconProps = {};

        // Dynamically handle all downloadStatus types
        if (downloadStatus === DownloadStatus.Completed) {
          trailingIconProps = {
            trailingIcon: {
              type: 'icon',
              source: 'delete',
              direction: 'trailing',
              onClick: () => {
                onLpDelete?.();
              },
            },
          };
        } else if (downloadStatus === DownloadStatus.Failed) {
          trailingIconProps = {
            trailingComponent: (
              <Icon
                name={'sync'}
                color="--md-sys-color-schemes-error"
                onControlledClick={() => {
                  onRetryDownloadLp?.();
                }}
              />
            ),
          };
        } else if (downloadStatus === DownloadStatus.None) {
          trailingIconProps = {
            trailingIcon: {
              type: 'icon',
              source: 'download',
              direction: 'trailing',
              cursor: 'pointer',
              onClick: () => {
                onTrailingIconClick?.({ data: lesson, index: index });
              },
            },
          };
        }
        return trailingIconProps;
      },
      [lessonPlan],
    );

    const listProps = {
      overlineText: status(lessonPlan.lessonPlanStatusClass) ? (
        <Typography variant="textLabelLargeSecondary" data-cy="lesson-last-open-date">
          {`Last Open ${formatUtcDate(status(lessonPlan.lessonPlanStatusClass))}`}
        </Typography>
      ) : (
        <Typography variant="textLabelLargeNeutral70" data-cy="lesson-not-opened">Not Opened</Typography>
      ),
      supportingText: removeUrduCharacters(lessonPlan.topic),
      heading: listHeading(
        lessonPlan,
        lpDownloadEnabled,
        lpDownloadAbTestEnabled,
        isConnected,
        bookInfo.index,
      ),
      buttonText: 'Open Lesson',
      buttonDataCy: 'open-lesson-button',
      buttonEnabled: true,
      onButtonClick: () => {
        onLpSelected?.({ data: lessonPlan, index: index });
      },
      onClick: () => {
        onLpSelected?.({ data: lessonPlan, index: index });
      },
      ...setTrailingIcon(lessonPlan),
      onTrailingIconClick: () => {},
      divider: lpDownloadAbTestEnabled && Capacitor.isNativePlatform(),
    };
    const renderDownloadButton = (lessonPlan: ILessonPlan, index: number) => (
      <ListExtended
        downloadButtonText={`Download Lesson ${bookInfo.index}`}
        icon="download"
        {...listProps}
        onExtentedButtonClick={() =>
          onTrailingIconClick?.({ data: lessonPlan, index })
        }
      />
    );

    const renderExtendedText = (
      lessonPlan: ILessonPlan,
      isConnected: boolean,
    ) => (
      <ListExtendedText
        extentedText={
          <>{lpExtendedMessage(lessonPlan, isConnected, bookInfo.index)}</>
        }
        {...listProps}
        extendedtrailingComponent={
          !DownloadingStatus.includes(
            lessonPlan.downloadStatus as DownloadStatus,
          ) && (
            <Button
              variant={
                lessonPlan.downloadStatus === DownloadStatus.Completed
                  ? 'deleted-text'
                  : 'text'
              }
              iconColor={
                DownloadStatusColor[
                  (lessonPlan?.downloadStatus as keyof typeof DownloadStatusIcon) ??
                    null
                ]
              }
              icon={
                DownloadStatusIcon[
                  (lessonPlan?.downloadStatus as keyof typeof DownloadStatusIcon) ??
                    null
                ]
              }
              width="fit-content"
              children={
                DownloadStatusText[
                  (lessonPlan.downloadStatus as keyof typeof DownloadStatusIcon) ??
                    null
                ]
              }
              onClick={() => {
                if (lessonPlan.downloadStatus === DownloadStatus.Completed)
                  onLpDelete?.();
                if (lessonPlan.downloadStatus === DownloadStatus.Failed)
                  onRetryDownloadLp?.();
              }}
            />
          )
        }
      />
    );

    if (lpDownloadAbTestEnabled && Capacitor.isNativePlatform()) {
      return lessonPlan.downloadStatus === DownloadStatus.None
        ? renderDownloadButton(lessonPlan, index)
        : renderExtendedText(lessonPlan, isConnected);
    } else {
      return (
        <>
          <List key={lessonPlan.id} {...listProps} />
          {lpCardMessages(lessonPlan, isConnected)}
        </>
      );
    }
  },
  (prevProps, nextProps) => {
    // Dynamically compare the downloadStatus of the lessonPlan objects
    return (
      prevProps.lessonPlan?.downloadProgress ===
        nextProps.lessonPlan?.downloadProgress &&
      prevProps.lessonPlan?.downloadStatus ===
        nextProps.lessonPlan?.downloadStatus
    );
  },
);

export const LpCardList: React.FC<LpCardListProps> = ({
  lessonPlans,
  chapterId,
  schoolClass,
  lpDownloadEnabled,
  lpDownloadAbTestEnable,
  onCancelLp,
  onLpSelected,
  onLpDelete,
  onTrailingIconClick,
  onRetryDownloadLp,
}) => {
  return (
    <Spacing paddingBottom="xl">
      <LpCardListWrapper>
        {lessonPlans.map((data, index) => {
          return (
            <div
              key={data.topic}
              data-tour={index === 0 ? 'first-lp-card' : undefined}
              style={{ position: 'relative' }}
            >
              <LpCardWrapper
                onLpSelected={onLpSelected}
                lessonPlan={data}
                key={data.id}
                index={index}
                schoolClass={schoolClass}
                lpDownloadEnabled={lpDownloadEnabled}
                lpDownloadAbTestEnabled={lpDownloadAbTestEnable}
                onLpDelete={() => onLpDelete?.(data)}
                onTrailingIconClick={(data) => {
                  onTrailingIconClick?.(data);
                }}
                onRetryDownloadLp={() => {
                  onRetryDownloadLp?.(data);
                }}
                onCancelLp={() => {
                  onCancelLp?.(data);
                }}
                chapterId={chapterId}
              />
            </div>
          );
        })}
      </LpCardListWrapper>
    </Spacing>
  );
};
